import React from "react";
import { ModeRendererProps } from "./types";

// VASARELY × ISSEY MIYAKE: Textile algorithms
// Pleated structures, modular folds, dynamic shadows
export function VasarelyIsseyMode({ brand }: ModeRendererProps) {
  return (
    <g>
      {/* Light textured background */}
      <defs>
        <linearGradient id="issey-texture" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#F5F5F5" />
          <stop offset="50%" stopColor="#E8E8E8" />
          <stop offset="100%" stopColor="#F5F5F5" />
        </linearGradient>
      </defs>

      <rect x="0" y="0" width="794" height="1123" fill="url(#issey-texture)" />

      <g transform="translate(397 540)">
        {/* Pleated structures (origami folds) */}
        {Array.from({ length: 24 }).map((_, i) => {
          const angle = (i * Math.PI * 2) / 24;
          const innerR = 80;
          const outerR = 220;
          const nextAngle = ((i + 1) * Math.PI * 2) / 24;
          
          return (
            <path
              key={i}
              d={`
                M 0 0
                L ${Math.cos(angle) * innerR} ${Math.sin(angle) * innerR}
                L ${Math.cos(angle) * outerR} ${Math.sin(angle) * outerR}
                L ${Math.cos(nextAngle) * outerR} ${Math.sin(nextAngle) * outerR}
                L ${Math.cos(nextAngle) * innerR} ${Math.sin(nextAngle) * innerR}
                Z
              `}
              fill={i % 2 === 0 ? "#D0D0D0" : "#E8E8E8"}
              stroke="#B0B0B0"
              strokeWidth="1"
              opacity="0.6"
            />
          );
        })}

        {/* Modular fold grid */}
        {Array.from({ length: 16 }).map((_, i) =>
          Array.from({ length: 20 }).map((_, j) => {
            const x = (i - 7.5) * 40;
            const y = (j - 9.5) * 38;
            const fold = Math.sin(i * 0.5) * Math.cos(j * 0.4) * 12;
            
            return (
              <rect
                key={`${i}-${j}`}
                x={x - 16 + fold}
                y={y - 16}
                width="32"
                height="32"
                fill="#C8C8C8"
                opacity="0.2"
              />
            );
          })
        )}

        {/* Dynamic shadow waves */}
        {Array.from({ length: 12 }).map((_, i) => {
          const y = -240 + i * 40;
          const points = Array.from({ length: 40 }).map((_, j) => {
            const x = -300 + j * 15;
            const wave = Math.sin(j * 0.3 + i * 0.4) * 25;
            return `${x},${y + wave}`;
          }).join(" ");
          
          return (
            <polyline
              key={i}
              points={points}
              fill="none"
              stroke="#A0A0A0"
              strokeWidth="2"
              opacity="0.3"
            />
          );
        })}

        {/* Compression metaphor circles */}
        {Array.from({ length: 16 }).map((_, i) => {
          const r = 40 + i * 15;
          const compressed = i % 3 === 0;
          
          return (
            <ellipse
              key={i}
              cx="0"
              cy="0"
              rx={r}
              ry={compressed ? r * 0.7 : r}
              fill="none"
              stroke="#B0B0B0"
              strokeWidth="1"
              opacity={0.35 - i * 0.018}
            />
          );
        })}

        {/* Algorithmic nodes */}
        {[0, 60, 120, 180, 240, 300].map((angle) => {
          const rad = (angle * Math.PI) / 180;
          const r = 190;
          
          return (
            <g key={angle}>
              {/* Folded pentagon */}
              {Array.from({ length: 5 }).map((_, k) => {
                const pAngle = angle + k * 72;
                const pRad = (pAngle * Math.PI) / 180;
                const nextPRad = ((angle + (k + 1) * 72) * Math.PI) / 180;
                
                return (
                  <path
                    key={k}
                    d={`
                      M ${Math.cos(rad) * r} ${Math.sin(rad) * r}
                      L ${Math.cos(rad) * r + Math.cos(pRad) * 18} ${Math.sin(rad) * r + Math.sin(pRad) * 18}
                      L ${Math.cos(rad) * r + Math.cos(nextPRad) * 18} ${Math.sin(rad) * r + Math.sin(nextPRad) * 18}
                      Z
                    `}
                    fill={brand.violet}
                    opacity="0.3"
                  />
                );
              })}
            </g>
          );
        })}

        {/* Central pleated core */}
        <circle cx="0" cy="0" r="60" fill="#D8D8D8" opacity="0.4" />
        {Array.from({ length: 12 }).map((_, i) => {
          const angle = (i * Math.PI * 2) / 12;
          return (
            <line
              key={i}
              x1="0"
              y1="0"
              x2={Math.cos(angle) * 60}
              y2={Math.sin(angle) * 60}
              stroke="#A0A0A0"
              strokeWidth="2"
              opacity="0.5"
            />
          );
        })}
      </g>

      {/* Issey Miyake text */}
      <text x="397" y="1010" textAnchor="middle" fontFamily="sans-serif" fontSize="11" fill="#666" opacity="0.7" fontStyle="italic">
        algorithmes textiles
      </text>
    </g>
  );
}
